/**
 * Documents API
 *
 * Functions for interacting with document endpoints.
 */

import { api } from './client';

// =============================================================================
// Types
// =============================================================================

export interface EventDocument {
  id: string;
  fileName: string;
  displayName: string | null;
  fileSize: number;
  mimeType: string;
  uploadedAt: string;
  uploader: {
    id: string;
    firstName: string;
    lastName: string;
  };
}

export interface ListDocumentsResponse {
  documents: EventDocument[];
}

export interface UploadDocumentResponse {
  message: string;
  document: EventDocument;
}

export interface DeleteDocumentResponse {
  message: string;
}

// =============================================================================
// API Functions
// =============================================================================

/**
 * List all documents for an event
 */
export async function listEventDocuments(eventId: string): Promise<ListDocumentsResponse> {
  const response = await api.get<ListDocumentsResponse>(`/events/${eventId}/documents`);
  return response.data;
}

/**
 * Upload a document to an event
 */
export async function uploadDocument(
  eventId: string,
  file: File,
  displayName?: string
): Promise<UploadDocumentResponse> {
  const formData = new FormData();
  formData.append('file', file);
  if (displayName) {
    formData.append('displayName', displayName);
  }

  const response = await api.post<UploadDocumentResponse>(
    `/events/${eventId}/documents`,
    formData,
    {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    }
  );
  return response.data;
}

/**
 * Download a document
 * Returns a Blob that can be used to create a download link
 */
export async function downloadDocument(documentId: string): Promise<Blob> {
  const response = await api.get<Blob>(`/documents/${documentId}/download`, {
    responseType: 'blob',
  });
  return response.data;
}

/**
 * Delete a document
 */
export async function deleteDocument(documentId: string): Promise<DeleteDocumentResponse> {
  const response = await api.delete<DeleteDocumentResponse>(`/documents/${documentId}`);
  return response.data;
}

/**
 * Trigger file download in browser
 */
export function triggerDownload(blob: Blob, fileName: string): void {
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
}
